import { useEffect, useState } from "react";
import ModalBasicLayout from "../../../Modal/ModalBasicLayout";
import Loader from "../../../Loaders/Loader";
import ContainerModalActions from "../../../Containers/ContainerModalActions";
import { useDispatchApp } from "../../../../lib/redux";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import { startGetProductsCatalogAdsCampaigns } from "../../../../actions/adsCampaigns";
import {
  BUTTON_GO_BACK,
  BUTTON_SAVE,
  EMPTY_RESULTS_SHOW,
  LOADING,
  MODAL_PRODUCT_LIST_TIKTOK_DESCRIPTION,
  MODAL_PRODUCT_LIST_TIKTOK_TITLE,
  SEARCH_PRODUCT_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import { useForm } from "react-hook-form";
import TextField from "../../../Form/TextField";
import { useDebounced } from "../../../../hooks/useDebounce";
import { DELAY_DEBOUNCE, TIKTOK } from "../../../../utils/constants";
import { Divider, Typography } from "@mui/material";
import Checkbox from "../../../Form/Checkbox";

const ModalProductListTikTok = ({
  modalOpen,
  onCloseModal = () => {},
  onCallbackSelect = () => {},
  integrationID,
  adAccountID,
  catalogID,
  selectedProducts = [],
}) => {
  const { control, watch } = useForm({
    defaultValues: {
      search: "",
    },
  });
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(selectedProducts);
  const [isLoading, setIsLoading] = useState(true);

  const search = watch("search");

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const onFetchProducts = async (name = "") => {
    setIsLoading(true);
    const response = await dispatch(
      startGetProductsCatalogAdsCampaigns({
        integrationID,
        adAccountID,
        catalogID,
        name,
        platform: TIKTOK,
      })
    );
    if (response.ok) {
      const data = response.data;
      const items = Array.isArray(data) ? data : (data?.data ?? data ?? []);
      if (search?.length > 0) {
        setProducts(
          items.filter((p) =>
            (p?.name || "").toLowerCase().includes(search.toLowerCase())
          )
        );
      } else {
        setProducts(items);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (modalOpen) {
      setSelected(selectedProducts);
      onFetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  const onSearchProducts = useDebounced((searchValue) => {
    onFetchProducts(searchValue);
  }, DELAY_DEBOUNCE);

  const onToggleProduct = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const onSave = () => {
    const selectedProductsResult = products.filter((p) =>
      selected.includes(p.id)
    );
    onCallbackSelect(selectedProductsResult);
    onCloseModal();
  };

  useEffect(() => {
    onSearchProducts(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="sm"
      title={t(MODAL_PRODUCT_LIST_TIKTOK_TITLE)}
      description={t(MODAL_PRODUCT_LIST_TIKTOK_DESCRIPTION)}
    >
      {isLoading ? (
        <Loader hasMessage={true} message={t(LOADING)} />
      ) : (
        <>
          <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
            <TextField
              name="search"
              variant="filled"
              label={t(SEARCH_PRODUCT_PLACEHOLDER)}
              control={control}
            />
          </Box>
          {products.length === 0 && (
            <Typography variant="body1" textAlign="center">
              {t(EMPTY_RESULTS_SHOW)}
            </Typography>
          )}
          {products.length > 0 && (
            <Box sx={{ maxHeight: 400, overflow: "auto" }}>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ width: 32 }} />
                <Box sx={{ flex: 1 }}>Product</Box>
                <Box sx={{ width: 100 }}>Price</Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {products.map((p) => (
                  <ProductItem
                    key={p.id}
                    product={p}
                    isSelected={selected.includes(p.id)}
                    onToggleProduct={onToggleProduct}
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
      <DialogActions sx={{ p: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: t(BUTTON_GO_BACK),
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: selected.length === 0,
            text: t(BUTTON_SAVE),
            type: "submit",
            onClick: onSave,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

const ProductItem = ({ product, isSelected, onToggleProduct }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: 48,
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ width: 32 }}>
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleProduct(product.id)}
            edge="end"
          />
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Typography variant="body2">{product.name}</Typography>
          <Typography variant="caption">SKU: {product.skuID}</Typography>
        </Box>
        <Box sx={{ width: 100 }}>
          <Typography variant="body2">{product.price}</Typography>
        </Box>
      </Box>
      <Divider />
    </Box>
  );
};

export default ModalProductListTikTok;
